import { NavigationItem } from "@/navigation/types";
import hubConfig from "@/navigation/hubNavigation";
import adminConfig from "@/navigation/adminNavigation";
import stochasticConfig from "@/navigation/stochasticNavigation";
import dashboardsConfig from "@/navigation/dashboardsNavigation";
import eventRegistrationConfig from "@/navigation/eventRegistrationNavigation";
import employeeDirectoryConfig from "@/navigation/employeeDirectoryNavigation";
import partnerNetworkConfig from "@/navigation/partnerNetworkNavigation";
import learningCenterConfig from "@/navigation/learningCenterNavigation";
import {
  HomeIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  UsersIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  ShoppingBagIcon,
  SparklesIcon,
  CubeIcon,
  FolderIcon,
  BanknotesIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";

export function getUnifiedNavigation(): NavigationItem[] {
  return [
    {
      name: "Hub",
      internalName: "hub",
      href: "#",
      icon: HomeIcon,
      current: false,
      children: hubConfig.navigation,
    },
    {
      name: "Business Intelligence",
      internalName: "dashboards",
      href: "#",
      icon: Squares2X2Icon,
      current: false,
      children: dashboardsConfig.navigation,
    },
    {
      name: "Lead Generation",
      internalName: "stochastic",
      href: "#",
      icon: EnvelopeIcon,
      current: false,
      children: stochasticConfig.navigation,
    },
    {
      name: "Internal Admin",
      internalName: "admin",
      href: "#",
      icon: Squares2X2Icon,
      current: false,
      permissions: [
        "administration-events",
        "administration-email-management",
        "administration-resource-library",
        "administration-companies",
        "administration-users",
        "administration-stochastic",
        // Salespeople hold none of the administration-* permissions, so without
        // this the whole group — and the Sales Portal inside it — stays hidden.
        "sales-portal",
      ],
      children: adminConfig.navigation,
      isCertainPathOnly: true,
    },
    {
      name: "Learning Center",
      internalName: "learning-center",
      href: "#",
      icon: AcademicCapIcon,
      current: false,
      children: learningCenterConfig.navigation,
      // Not yet GA: visible to everyone in dev/staging, and to CertainPath
      // staff in production. In prod this flag is true → the staff-only
      // filter applies; in non-prod it's false → the gate is off for everyone.
      // Gates on the employee's own company so coaches keep it while
      // switched into client companies.
      isCertainPathEmployeeOnly:
        import.meta.env.VITE_ENVIRONMENT === "production",
    },
    {
      name: "Event Registration",
      internalName: "event-registration",
      href: "#",
      icon: CalendarDaysIcon,
      current: false,
      children: eventRegistrationConfig.navigation,
    },
    {
      name: "Shop",
      internalName: "shop",
      href: "#",
      icon: ShoppingBagIcon,
      current: false,
      children: [
        {
          name: "Total Access",
          href: "/subscriptions/total-access",
          icon: SparklesIcon,
          current: false,
          internalName: "total-access",
        },
        {
          name: "Products",
          href: "/product-directory",
          icon: CubeIcon,
          current: false,
          internalName: "product-directory-browse",
        },
      ],
    },
    {
      name: "Employee Directory",
      internalName: "employee-directory",
      href: "#",
      icon: UserGroupIcon,
      current: false,
      children: employeeDirectoryConfig.navigation,
    },
    {
      name: "Partner Network",
      internalName: "partner-network",
      href: "#",
      icon: BanknotesIcon,
      current: false,
      permissions: ["partner-network", "partner-network-admin"],
      children: partnerNetworkConfig.navigation,
    },
    {
      name: "Admin",
      internalName: "company-admin",
      href: "#",
      icon: Cog6ToothIcon,
      current: false,
      permissions: [
        "user-management",
        "company-profile",
        "integrations",
        "annual-revenue",
        "file-manager",
      ],
      children: [
        {
          name: "User Management",
          href: "/hub/users",
          icon: UsersIcon,
          current: false,
          internalName: "company_user_management",
          permissions: ["user-management"],
        },
        {
          name: "Company Settings",
          href: "/hub/settings",
          icon: Cog6ToothIcon,
          current: false,
          internalName: "company_settings",
          permissions: ["company-profile", "integrations", "annual-revenue"],
        },
        {
          name: "File Manager",
          href: "#file-manager",
          icon: FolderIcon,
          current: false,
          internalName: "file_manager",
          permissions: ["file-manager"],
          isDialog: true,
          dialogAction: "fileManager",
        },
      ],
    },
  ];
}
